import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Building2, MessageSquare, LogOut, Menu,
  Star, FileText, KeyRound, Image as ImageIcon,
} from 'lucide-react';
import { getCurrentAdmin, changeAdminPassword } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

// Tab components (refactored from this file into /pages/admin/*)
import DashboardOverview from '@/pages/admin/DashboardOverview';
import PropertiesManagement from '@/pages/admin/PropertiesManagement';
import InquiriesManagement from '@/pages/admin/InquiriesManagement';
import ReviewsManagement from '@/pages/admin/ReviewsManagement';
import BlogManagement from '@/pages/admin/BlogManagement';
import SiteImagesManagement from '@/pages/admin/SiteImagesManagement';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwSubmitting, setPwSubmitting] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('lalita_admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }
      try {
        const res = await getCurrentAdmin();
        setAdmin(res.data);
      } catch (err) {
        localStorage.removeItem('lalita_admin_token');
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('lalita_admin_token');
    navigate('/admin/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      toast.error('New password and confirmation do not match');
      return;
    }
    if (pwForm.next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setPwSubmitting(true);
    try {
      await changeAdminPassword(pwForm.current, pwForm.next);
      toast.success('Password changed successfully. Please sign in again.');
      setShowPasswordModal(false);
      setPwForm({ current: '', next: '', confirm: '' });
      // Force re-login for security
      setTimeout(() => {
        localStorage.removeItem('lalita_admin_token');
        navigate('/admin/login');
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwSubmitting(false);
    }
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/properties', label: 'Properties', icon: Building2 },
    { path: '/admin/inquiries', label: 'Inquiries', icon: MessageSquare },
    { path: '/admin/reviews', label: 'Reviews', icon: Star },
    { path: '/admin/blog', label: 'Blog', icon: FileText },
    { path: '/admin/site-images', label: 'Site Images', icon: ImageIcon },
  ];

  const isActive = (path) => location.pathname === path;

  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-sand">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-ubud-jungle"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-sand flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-full flex flex-col">
          <div className="p-6 border-b">
            <Link to="/" className="flex items-center">
              <span className="font-heading text-xl font-semibold text-charcoal">Lalita</span>
              <span className="font-heading text-xl text-ubud-jungle ml-1">Admin</span>
            </Link>
          </div>

          <nav className="flex-1 p-4">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.path.split('/').pop()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                  isActive(item.path)
                    ? 'bg-ubud-jungle text-white'
                    : 'text-charcoal-light hover:bg-warm-sand'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-ubud-jungle text-white rounded-full flex items-center justify-center font-medium">
                {admin.name?.[0] || 'A'}
              </div>
              <div>
                <div className="font-medium text-charcoal text-sm">{admin.name}</div>
                <div className="text-charcoal-muted text-xs">{admin.email}</div>
              </div>
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center gap-2 text-charcoal-muted hover:text-ubud-jungle text-sm w-full mb-2"
              data-testid="admin-change-password-btn"
            >
              <KeyRound className="w-4 h-4" />
              Change Password
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-charcoal-muted hover:text-red-600 text-sm w-full"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 lg:ml-64">
        <header className="bg-white shadow-sm px-4 py-4 flex items-center justify-between lg:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-warm-sand"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              target="_blank"
              className="text-sm text-ubud-jungle hover:underline"
            >
              View Site →
            </Link>
          </div>
        </header>

        <main className="p-4 lg:p-8">
          <Routes>
            <Route path="/dashboard" element={<DashboardOverview />} />
            <Route path="/properties" element={<PropertiesManagement />} />
            <Route path="/inquiries" element={<InquiriesManagement />} />
            <Route path="/reviews" element={<ReviewsManagement />} />
            <Route path="/blog" element={<BlogManagement />} />
            <Route path="/site-images" element={<SiteImagesManagement />} />
            <Route path="*" element={<DashboardOverview />} />
          </Routes>
        </main>
      </div>

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Current Password</label>
              <Input
                type="password"
                value={pwForm.current}
                onChange={(e) => setPwForm((p) => ({ ...p, current: e.target.value }))}
                required
                autoComplete="current-password"
                data-testid="change-password-current"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">New Password</label>
              <Input
                type="password"
                value={pwForm.next}
                onChange={(e) => setPwForm((p) => ({ ...p, next: e.target.value }))}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="change-password-new"
              />
              <p className="text-xs text-charcoal-muted mt-1">Minimum 8 characters, must include letters and numbers.</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Confirm New Password</label>
              <Input
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="change-password-confirm"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPasswordModal(false)} disabled={pwSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={pwSubmitting} className="bg-ubud-jungle hover:bg-ubud-jungle-dark" data-testid="change-password-submit">
                {pwSubmitting ? 'Updating…' : 'Update Password'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
